/**
 * Booking Link page (Settings › Online Booking): one "How do clients book online?" choice (Hayven online booking,
 * another booking website, or none) that decides where every Book button goes. The URL keeps its old
 * `setup-integration` path so existing in-app links and bookmarks still land here. A restaurant sees its online-ordering
 * link instead (no Hayven booking, so no booking-page banners).
 */

import { createFileRoute, useParams } from '@tanstack/react-router'
import { Helmet } from 'react-helmet-async'
import { SettingsPageHeader } from '@/components/ui'
import { BusinessSetupPageShell } from './business-setup/components/business-setup-page-shell'
import { BookingLinkCard } from './online-booking/components/booking-link-card'
import { OnlineBookingReadinessBanner } from './online-booking/components/online-booking-readiness-banner'
import { BookingsOnPosNotice } from './online-booking/components/bookings-on-pos-notice'
import { useOnlineBookingSettings } from './online-booking/hooks/use-online-booking-settings'
import { useSalonSetupMeanings } from '@/features/salon-setup/use-salon-setup-meanings'
import { formatDocumentTitle } from '@/lib/document-title'
// Temporarily hidden — re-enable by restoring this import and the <WebsiteIntegrationCard /> below.
// Any embed that frames the booking page must first allow it: apps/booking/next.config.ts denies all framing.
// import { WebsiteIntegrationCard } from './online-booking/components/website-integration-card'

export const Route = createFileRoute('/_auth/$orgId/settings/online-booking/setup-integration')({
  component: SetupIntegrationPage,
})

function SetupIntegrationPage() {
  const { orgId } = useParams({ from: '/_auth/$orgId/settings/online-booking/setup-integration' })
  // Calendar off: Hayven online booking can't run, so the page explains the salon's own booking link instead.
  const { bookingsOutsideHayven, bookingsOnPos } = useSalonSetupMeanings()
  // Same cached read as the card below.
  const ordering = useOnlineBookingSettings().data?.data.bookingAction === 'ORDER'

  return (
    <>
      <Helmet defer={false}>
        <title>{formatDocumentTitle('Booking Link', 'Online Booking')}</title>
      </Helmet>

      <BusinessSetupPageShell orgId={orgId} activePath="setup-integration">
        <SettingsPageHeader
          title={ordering ? 'Ordering Link' : 'Booking Link'}
          description={ordering ? 'Where guests go when they tap an Order online button.' : 'Where clients go when they tap a Book button.'}
        />

        <div className="space-y-6 max-w-2xl">
          {ordering ? null : bookingsOutsideHayven ? (
            <BookingsOnPosNotice routeOrgId={orgId} bookingsOnPos={bookingsOnPos} />
          ) : (
            <OnlineBookingReadinessBanner />
          )}
          <BookingLinkCard />
          {/* Temporarily hidden — restore this line (and its import above) to show the card again. */}
          {/* <WebsiteIntegrationCard /> */}
        </div>
      </BusinessSetupPageShell>
    </>
  )
}
